import React from 'react'
import styled from '@emotion/styled'
import { mediaQueries } from '@src/styles/theme'
import Highlight, { defaultProps, Language } from 'prism-react-renderer'
import { css } from '@emotion/core'
import Img from 'gatsby-image'
import { Link } from 'gatsby'
import { Text } from '@src/components/Text'
import { FaGithub, FaTwitter } from 'react-icons/fa'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { MDXProvider } from '@mdx-js/react'
import { Box, Flex } from '@src/components/Boxes'
import slugify from 'slugify'
import SocialSharing from '@src/components/blog/SocialSharing'
import theme from 'prism-react-renderer/themes/nightOwl'

const Article = styled('article')`
  position: relative;
  background-color: #fff;
  padding: 65px 15px;
  margin: 0 -15px 0px -15px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;

  ${mediaQueries[0]} {
    padding: 60px;
    margin: 90px 0 0;
  }
`

const ArticleHeaderBox = styled('div')`
  display: block;
  margin-bottom: 35px;

  ${mediaQueries[1]} {
    margin-bottom: 60px;
  }
`

const articleHeaderImageStyle = css`
  border-radius: 50%;
  width: 60px;
  height: 60px;
  min-width: 60px;
  min-height: 60px;
  max-width: 60px;
  max-height: 60px;
  margin: 0 auto;
  position: absolute !important;
  top: -30px;
  left: 0;
  right: 0;
`

const ArticleHeader = styled('h1')`
  cursor: pointer;

  text-align: center;
  font-weight: 400;
  background-color: transparent;
  color: #333;
  text-decoration: none;

  :hover {
    color: #00aeef;
  }

  font-size: 27px;
  line-height: 1.2em;

  ${mediaQueries[1]} {
    font-size: 30px;
    line-height: 36px;
  }
`

const ArticleHeaderMeta = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 15px;
  font-weight: 400;
  color: #777777;
  p {
    margin-bottom: 0;
  }

  a {
    color: #777777;
    :hover {
      color: #00aeef;
    }
  }
`

type ArticleImageProps = { maxWidth: number }
const ArticleImage = styled(Img)<ArticleImageProps>`
  width: 100%;
  margin-bottom: 35px;
  margin-left: auto;
  margin-right: auto;
  max-width: ${props => (props.maxWidth && props.maxWidth < 400 ? props.maxWidth + 'px' : '100%')};
`

const ArticleMoreLink = styled(Link)`
  font-size: 14px;
  line-height: 27px;
  font-weight: 400;
  background-color: transparent;
  color: #464646;
  display: block;
  text-align: center;
  text-decoration: none;

  span {
    display: inline-block;
    padding: 15px;
    background: #fff;
    position: relative;
    z-index: 2;
  }

  :hover {
    color: #00aeef;
    outline: 0;
    text-decoration: none;
  }

  :after {
    content: '';
    border-bottom: 1px dashed #ccc;
    display: block;
    position: relative;
    top: -27px;
  }

  :hover:after {
    border-bottom: 1px dashed #00aeef;
  }
`

const categoryButtonStyles = css`
  display: inline-block;
  padding: 3px 10px;
  background-color: #f4f4f4;
  color: #868686;
  border-radius: 0px;
  font-size: 13px;
  line-height: 22px;
  text-decoration: none;
  margin-bottom: 8px;
  margin-right: 8px;
  text-transform: capitalize;
  font-weight: 300;
  border: 1px solid transparent;

  :hover {
    text-decoration: none;
    background-color: #eaf9ff;
    color: #0089bc;
    border: 1px solid #00aeef;
    outline: 0;
  }
`

const AuthorBio = styled(Flex)`
  border-radius: 0;
  background: #fff;
  border-left: none;
  border-right: none;
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  box-shadow: none;
  padding: 19px 0;
  margin-top: 40px;
  margin-bottom: 40px;
`
AuthorBio.defaultProps = {}
const authorImgStyle = css`
  border-radius: 50%;
  width: 60px;
  height: 60px;
  min-width: 60px;
  min-height: 60px;
  max-width: 60px;
  max-height: 60px;
  margin: 0 auto;
`
const AuthorLink = styled(Link)`
  margin-bottom: 5px;
  color: #00aeef;
  :hover {
    color: #0076a3;
    text-decoration: underline;
  }
`
const AuthorTwitterLink = styled('a')`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  font-size: 18px;
  color: #464646;
  margin: 0 4px;
  background: #f4f4f4;
  padding: 7px;
  :hover {
    color: #00aeef;
  }
`

const MdxH1 = props => (
  <h1
    css={css`
      font-size: 34px;
      font-weight: 400;
      line-height: 35px;
      margin-bottom: 10px;
      margin-top: 20px;
      color: #333333;
    `}
    {...props}
  />
)
const MdxH2 = props => (
  <h2
    css={css`
      font-size: 30px;
      font-weight: 400;
      line-height: 33px;
      margin-bottom: 10px;
      margin-top: 20px;
      color: #333333;
    `}
    {...props}
  />
)
const MdxH3 = props => (
  <h3
    css={css`
      font-size: 28px;
      font-weight: 400;
      line-height: 31px;
      margin-bottom: 10px;
      margin-top: 20px;
      color: #333333;
    `}
    {...props}
  />
)
const MdxH4 = props => (
  <h4
    css={css`
      font-size: 22px;
      font-weight: 400;
      line-height: 28px;
      margin-bottom: 10px;
      margin-top: 20px;
      color: #333333;
    `}
    {...props}
  />
)
const MdxH5 = props => (
  <h5
    css={css`
      font-size: 18px;
      font-weight: 400;
      line-height: 24px;
      margin-bottom: 10px;
      margin-top: 26px;
      color: #333333;
    `}
    {...props}
  />
)
const MdxP = props => (
  <p
    css={css`
      font-size: 14px;
      hyphens: auto;
      line-height: 27px;
      margin: 0 0 15px;
      word-break: break-word;
      color: #333333;
      font-weight: 400;
    `}
    {...props}
  />
)
const MdxA = props => (
  <a
    css={css`
      background-color: transparent;
      color: #00aeef;
      text-decoration: none;

      :hover {
        color: #0076a3;
        outline: 0;
        text-decoration: underline;
      }
    `}
    {...props}
  />
)
const MdxImg = props => (
  <img
    css={css`
      margin-left: auto;
      margin-right: auto;
      display: block;
      margin-bottom: 35px;

      max-width: 75%;
      object-fit: contain;
      position: relative;
    `}
    {...props}
  />
)
const MdxUl = props => (
  <ul
    css={css`
      font-size: 14px;
      hyphens: auto;
      line-height: 27px;
      margin: 0 0 15px;
      word-break: break-word;
      color: #333333;
      font-weight: 400;
    `}
    {...props}
  />
)
const MdxOl = props => (
  <ol
    css={css`
      font-size: 14px;
      hyphens: auto;
      line-height: 27px;
      margin: 0 0 15px;
      word-break: break-word;
      color: #333333;
      font-weight: 400;
    `}
    {...props}
  />
)
const CodeBlock = ({ children, className }) => {
  const language: Language = className && className.indexOf('language-') !== -1 ? className.replace(/language-/, '') : 'bash'

  const LineNo = styled.span`
    display: inline-block;
    width: 1.5em;
    user-select: none;
    opacity: 0.3;
    margin-right: 10px;
    border-right: beige thin solid;
  `
  return (
    <Highlight {...defaultProps} code={children.trim()} language={language} theme={theme}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <pre className={className} style={{ ...style, padding: '20px 20px 20px 20px', whiteSpace: 'pre-wrap' }}>
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line, key: i })}>
              <LineNo>{i + 1}</LineNo>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token, key })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  )
}
const InlineCode = props => (
  <code
    css={css`
      color: #c7254e;
      font-family: SourceCodePro, Menlo, Monaco, 'Andale Mono', 'lucida console', 'Courier New', monospace !important;
      border-radius: 5px;
      margin: 0 3px;
      border: 1px solid #eee;
      padding: 1px 6px;
      background: none repeat scroll 0 0 #f2f2f2;
    `}
    {...props}
  />
)
const Pre = props => (
  <pre
    css={css`
      font-size: 12px;
      font-family: SourceCodePro, Menlo, Monaco, 'Andale Mono', 'lucida console', 'Courier New', monospace !important;
    `}
    {...props}
  />
)

const mdxComponents = {
  h1: MdxH1,
  h2: MdxH2,
  h3: MdxH3,
  h4: MdxH4,
  h5: MdxH5,
  p: MdxP,
  a: MdxA,
  img: MdxImg,
  ul: MdxUl,
  ol: MdxOl,
  code: CodeBlock,
  inlineCode: InlineCode,
  pre: Pre
}

const Youtube = ({ id }) => (
  <iframe width="500" height="281" src={`https://www.youtube.com/embed/${id}?feature=oembed`} frameBorder="0" allowFullScreen />
)

const Row = ({ children }) => <Flex>{children}</Flex>
const Column = ({ children }) => (
  <Box
    width="100%"
    css={css`
      img {
        max-width: 100% !important;
      }
    `}
  >
    {children}
  </Box>
)

const mdxShortcodes = { Youtube, Row, Column }

type BlogArticleProps = { post: any; showExcerpt?: boolean }
const BlogArticle: React.FC<BlogArticleProps> = ({ post, showExcerpt }) => {
  if (post.node) {
    post = post.node
  }

  function getPostPath(post) {
    if (post.for_website === 'dev-blog') return `/${post.year}/${post.month}/${post.day}/${post.slug}`
    else return `/${post.slug}`
  }

  return (
    <Article>
      <Img fixed={post.author.image.localFile.childImageSharp.fixed} css={articleHeaderImageStyle} alt="author" />
      <ArticleHeaderBox>
        <Link to={getPostPath(post)}>
          <ArticleHeader>{post.title}</ArticleHeader>
        </Link>
        <ArticleHeaderMeta>
          <Link to={`/author/${post.author.slug}`}>
            <Text display="inline">{post.author.name}</Text>
          </Link>
          {post.author.twitter_nick && (
            <a href={`https://twitter.com/${post.author.twitter_nick.toLowerCase()}`}>
              <Flex alignItems="center" marginLeft="15px">
                <FaTwitter />
                <Text display="inline">{post.author.twitter_nick}</Text>
              </Flex>
            </a>
          )}
          {post.author.github_nick && (
            <a href={`https://github.com/${post.author.github_nick}`}>
              <Flex alignItems="center" marginLeft="15px">
                <FaGithub />
                <Text display="inline">{post.author.github_nick}</Text>
              </Flex>
            </a>
          )}
          <Text marginLeft="15px" display="inline">
            {post.publish_date}
          </Text>
        </ArticleHeaderMeta>
      </ArticleHeaderBox>
      {post.image && (
        <Link to={getPostPath(post)}>
          <ArticleImage fluid={post.image.localFile.childImageSharp.fluid} maxWidth={post.image.width} loading="eager" />
        </Link>
      )}

      <MDXProvider components={{ ...mdxComponents, ...mdxShortcodes }}>
        <MDXRenderer>
          {post.fields.hasExcerpt && showExcerpt
            ? post.childDirectusBlogPostExcerpt.childMdx.body
            : post.childDirectusBlogPostBody.childMdx.body}
        </MDXRenderer>
      </MDXProvider>

      {post.fields.hasExcerpt && showExcerpt && (
        <ArticleMoreLink to={getPostPath(post)}>
          <span>Continue reading</span>
        </ArticleMoreLink>
      )}

      {!showExcerpt && <SocialSharing image={post.image} />}

      {post.author.bio && !showExcerpt && (
        <AuthorBio>
          <Img fixed={post.author.image.localFile.childImageSharp.fixed} css={authorImgStyle} alt="author" />
          <Flex flexDirection={'column'} fontSize="14px" color="#626262" lineHeight={1.3} marginLeft="10px">
            <AuthorLink to={`/author/${post.author.slug}`}>{post.author.name}</AuthorLink>
            <Text marginBottom="15px">{post.author.bio}</Text>
            {post.author.twitter_nick && (
              <AuthorTwitterLink href={`https://twitter.com/${post.author.twitter_nick}`}>
                <FaTwitter />
              </AuthorTwitterLink>
            )}
          </Flex>
        </AuthorBio>
      )}

      <Flex marginTop="20px" flexWrap="wrap">
        {post.categories &&
          post.categories.map((category, ci) => (
            <Link to={`/category/${slugify(category.slug)}`} key={`article-${post.slug}-category-${ci}`} css={categoryButtonStyles}>
              {category.name}
            </Link>
          ))}
      </Flex>
    </Article>
  )
}

export default BlogArticle
